import { useState, useEffect } from "react";
import { useSelector } from "react-redux";

import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Typography,
  Select as MuiSelect,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  TextField,
  Autocomplete,
} from "@mui/material";
import { parseCsvText, toCsvText } from "../../utils/csvUtils";
import useData from "../../hooks/useData";
import usePage from "../../hooks/usePage";

const LocationImport = ({ onClose, onComplete }) => {
  const { executeAction, getFromServer } = useData();
  const { showMessage } = usePage();

  const email = useSelector((state) => state.authentication.email);

  const [step, setStep] = useState("upload"); // upload, mapping, defaults, processing
  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [columnMapping, setColumnMapping] = useState({
    bannerName: "",
    locationNumber: "",
    locationName: "",
    format: "",
    territory: "",
    addressFull: "",
    // Or structured:
    streetAddress: "",
    city: "",
    province: "",
    postal: "",
  });
  const [batchName, setBatchName] = useState("");
  const [defaultBanner, setDefaultBanner] = useState("");
  const [defaultProvince, setDefaultProvince] = useState("");
  const [notes, setNotes] = useState("");
  const [banners, setBanners] = useState([]);

  useEffect(() => {
    loadBanners();
  }, []);

  const loadBanners = async () => {
    try {
      const result = await getFromServer({
        entityName: "Banner",
      });

      if (result) {
        setBanners(result);
      }
    } catch (error) {
      console.error("[LocationImport] Error loading banners:", error);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setCsvFile(file);
      await parseCSV(file);
    }
  };

  const parseCSV = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const utf8Text = new TextDecoder("utf-8", { fatal: false }).decode(
        arrayBuffer,
      );
      const needsFallback = utf8Text.includes("�");
      const csvText = needsFallback
        ? new TextDecoder("windows-1252", { fatal: false }).decode(arrayBuffer)
        : utf8Text;

      const results = parseCsvText(csvText, {
        header: true,
        skipEmptyLines: true,
      });
      setCsvData(results.data);
      setCsvHeaders(results.meta.fields || []);
      setStep("mapping");
    } catch (error) {
      showMessage(`Error reading CSV: ${error.message}`, "error");
    }
  };

  const handleMappingChange = (field, value) => {
    setColumnMapping((prev) => ({ ...prev, [field]: value }));
  };

  const handleNextToDefaults = () => {
    if (!columnMapping.addressFull && !columnMapping.postal) {
      showMessage(
        "Address field or Postal code is required for matching",
        "error",
      );
      return;
    }
    setStep("defaults");
  };

  const handleStartProcessing = async () => {
    setStep("processing");

    try {
      const finalBatchName =
        batchName || `Import ${new Date().toLocaleDateString()}`;

      const results = await executeAction({
        entityName: "LocationImport",
        action: "upload",
        data: {
          csvData: toCsvText(csvData),
          columnMapping,
          batchName: finalBatchName,
          defaultBanner: defaultBanner || null,
          defaultProvince: defaultProvince || null,
          notes: notes || null,
          email,
        },
      });

      if (results?.isSuccessful && results.data?.batchId) {
        showMessage(
          `Import batch "${finalBatchName}" submitted successfully. You will receive an email when ready for review.`,
        );

        setStep("upload");
        setCsvData([]);
        setCsvHeaders([]);
        setCsvFile(null);
        setBatchName("");
        setDefaultBanner("");
        setDefaultProvince("");
        setNotes("");

        onClose();
        if (onComplete) {
          onComplete(results.data.batchId);
        }
      } else {
        showMessage("Upload failed: no batchId returned", "error");
        setStep("defaults");
      }
    } catch (error) {
      showMessage(`Upload failed: ${error.message}`, "error");
      setStep("defaults");
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="xl"
      fullWidth
    >
      <DialogTitle>Import Location File</DialogTitle>
      <DialogContent>
        {step === "upload" && (
          <Box sx={{ padding: 2 }}>
            <Typography
              variant="body2"
              gutterBottom
            >
              Upload a CSV file containing locations. The file can have
              addresses in a single field or in separate columns.
            </Typography>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
            />
          </Box>
        )}

        {step === "mapping" && (
          <Box sx={{ padding: 2 }}>
            <Typography
              variant="h6"
              gutterBottom
            >
              Map CSV Columns
            </Typography>
            <Typography
              variant="body2"
              gutterBottom
              sx={{ mb: 2 }}
            >
              Match your CSV columns to the required fields. Provide either a
              full address field OR separate address components.
            </Typography>
            <Stack spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Banner Name</InputLabel>
                <MuiSelect
                  value={columnMapping.bannerName}
                  onChange={(e) =>
                    handleMappingChange("bannerName", e.target.value)
                  }
                  label="Banner Name"
                >
                  <MenuItem value="">
                    <em>Not mapped</em>
                  </MenuItem>
                  {csvHeaders.map((header) => (
                    <MenuItem
                      key={header}
                      value={header}
                    >
                      {header}
                    </MenuItem>
                  ))}
                </MuiSelect>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Location Number</InputLabel>
                <MuiSelect
                  value={columnMapping.locationNumber}
                  onChange={(e) =>
                    handleMappingChange("locationNumber", e.target.value)
                  }
                  label="Location Number"
                >
                  <MenuItem value="">
                    <em>Not mapped</em>
                  </MenuItem>
                  {csvHeaders.map((header) => (
                    <MenuItem
                      key={header}
                      value={header}
                    >
                      {header}
                    </MenuItem>
                  ))}
                </MuiSelect>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Location Name</InputLabel>
                <MuiSelect
                  value={columnMapping.locationName}
                  onChange={(e) =>
                    handleMappingChange("locationName", e.target.value)
                  }
                  label="Location Name"
                >
                  <MenuItem value="">
                    <em>Not mapped</em>
                  </MenuItem>
                  {csvHeaders.map((header) => (
                    <MenuItem
                      key={header}
                      value={header}
                    >
                      {header}
                    </MenuItem>
                  ))}
                </MuiSelect>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Format</InputLabel>
                <MuiSelect
                  value={columnMapping.format}
                  onChange={(e) =>
                    handleMappingChange("format", e.target.value)
                  }
                  label="Format"
                >
                  <MenuItem value="">
                    <em>Not mapped</em>
                  </MenuItem>
                  {csvHeaders.map((header) => (
                    <MenuItem
                      key={header}
                      value={header}
                    >
                      {header}
                    </MenuItem>
                  ))}
                </MuiSelect>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Territory (Optional)</InputLabel>
                <MuiSelect
                  value={columnMapping.territory}
                  onChange={(e) =>
                    handleMappingChange("territory", e.target.value)
                  }
                  label="Territory (Optional)"
                >
                  <MenuItem value="">
                    <em>Not mapped</em>
                  </MenuItem>
                  {csvHeaders.map((header) => (
                    <MenuItem
                      key={header}
                      value={header}
                    >
                      {header}
                    </MenuItem>
                  ))}
                </MuiSelect>
              </FormControl>

              <Typography
                variant="subtitle2"
                sx={{ mt: 2 }}
              >
                Address (choose one option):
              </Typography>

              <FormControl fullWidth>
                <InputLabel>Full Address (Single Field)</InputLabel>
                <MuiSelect
                  value={columnMapping.addressFull}
                  onChange={(e) =>
                    handleMappingChange("addressFull", e.target.value)
                  }
                  label="Full Address (Single Field)"
                >
                  <MenuItem value="">
                    <em>Not mapped</em>
                  </MenuItem>
                  {csvHeaders.map((header) => (
                    <MenuItem
                      key={header}
                      value={header}
                    >
                      {header}
                    </MenuItem>
                  ))}
                </MuiSelect>
              </FormControl>

              <Typography
                variant="body2"
                sx={{ textAlign: "center" }}
              >
                — OR —
              </Typography>

              <FormControl fullWidth>
                <InputLabel>Street Address</InputLabel>
                <MuiSelect
                  value={columnMapping.streetAddress}
                  onChange={(e) =>
                    handleMappingChange("streetAddress", e.target.value)
                  }
                  label="Street Address"
                  disabled={!!columnMapping.addressFull}
                >
                  <MenuItem value="">
                    <em>Not mapped</em>
                  </MenuItem>
                  {csvHeaders.map((header) => (
                    <MenuItem
                      key={header}
                      value={header}
                    >
                      {header}
                    </MenuItem>
                  ))}
                </MuiSelect>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>City</InputLabel>
                <MuiSelect
                  value={columnMapping.city}
                  onChange={(e) => handleMappingChange("city", e.target.value)}
                  label="City"
                  disabled={!!columnMapping.addressFull}
                >
                  <MenuItem value="">
                    <em>Not mapped</em>
                  </MenuItem>
                  {csvHeaders.map((header) => (
                    <MenuItem
                      key={header}
                      value={header}
                    >
                      {header}
                    </MenuItem>
                  ))}
                </MuiSelect>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Province</InputLabel>
                <MuiSelect
                  value={columnMapping.province}
                  onChange={(e) =>
                    handleMappingChange("province", e.target.value)
                  }
                  label="Province"
                  disabled={!!columnMapping.addressFull}
                >
                  <MenuItem value="">
                    <em>Not mapped</em>
                  </MenuItem>
                  {csvHeaders.map((header) => (
                    <MenuItem
                      key={header}
                      value={header}
                    >
                      {header}
                    </MenuItem>
                  ))}
                </MuiSelect>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Postal Code</InputLabel>
                <MuiSelect
                  value={columnMapping.postal}
                  onChange={(e) =>
                    handleMappingChange("postal", e.target.value)
                  }
                  label="Postal Code"
                  disabled={!!columnMapping.addressFull}
                >
                  <MenuItem value="">
                    <em>Not mapped</em>
                  </MenuItem>
                  {csvHeaders.map((header) => (
                    <MenuItem
                      key={header}
                      value={header}
                    >
                      {header}
                    </MenuItem>
                  ))}
                </MuiSelect>
              </FormControl>
            </Stack>
          </Box>
        )}

        {step === "defaults" && (
          <Box sx={{ padding: 2 }}>
            <Typography
              variant="h6"
              gutterBottom
            >
              Set Default Values
            </Typography>
            <Typography
              variant="body2"
              gutterBottom
              sx={{ mb: 2 }}
            >
              Provide default values to apply to all imported records
              (optional).
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="Batch Name"
                fullWidth
                value={batchName}
                onChange={(e) => setBatchName(e.target.value)}
                placeholder="e.g., Import 2026-02-04"
              />

              <TextField
                label="Batch Notes"
                fullWidth
                multiline
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional notes about this import"
              />

              <Typography
                variant="subtitle2"
                sx={{ mt: 2 }}
              >
                Default Values (if not in CSV):
              </Typography>

              <FormControl fullWidth>
                <Autocomplete
                  freeSolo
                  value={defaultBanner || ""}
                  onInputChange={(event, newValue) => {
                    setDefaultBanner(newValue);
                  }}
                  onChange={(event, newValue) => {
                    setDefaultBanner(newValue || "");
                  }}
                  options={banners.map((banner) => banner.name)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Default Banner (if missing in CSV)"
                      placeholder="Start typing a banner name"
                    />
                  )}
                />
              </FormControl>

              <TextField
                label="Default Province (if missing in CSV)"
                fullWidth
                value={defaultProvince}
                onChange={(e) => setDefaultProvince(e.target.value)}
                placeholder="e.g., ON, BC, QC"
              />
            </Stack>
          </Box>
        )}

        {step === "processing" && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: 4,
            }}
          >
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Processing import...</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {step === "upload" && <Button onClick={onClose}>Cancel</Button>}
        {step === "mapping" && (
          <>
            <Button onClick={() => setStep("upload")}>Back</Button>
            <Button
              onClick={handleNextToDefaults}
              variant="contained"
            >
              Next
            </Button>
          </>
        )}
        {step === "defaults" && (
          <>
            <Button onClick={() => setStep("mapping")}>Back</Button>
            <Button
              onClick={handleStartProcessing}
              variant="contained"
              color="primary"
              disabled={step === "processing"}
            >
              Submit Import
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default LocationImport;
